const {KitModule, registerModule} = window;

class PostListingSearch extends KitModule {
  constructor($element) {
    super($element);

    this.setVars();
    this.bindEvents();

    // Bind events for htmx
    document.body.addEventListener('htmx:afterSwap', () => {
      this.setVars();
      this.bindEvents();
    });

    const script = document.createElement('script');
    script.src = '/static/js/vendor/htmx@1.9.10.min.js';
    document.head.appendChild(script);

    this.htmx = window.htmx;
  }

  setVars() {
    this.initializing = false;
    this.pagefind = undefined;
    this.timer = 0;
    this.searched = false;
    this.loading = false;
    this.debounceTimeoutMs = 300;
    this.searchTerm = '';
    this.searchId = 0;
    this.searchResult = [];
    this.pageSize = 10;
    this.show = this.pageSize;

    this.$searchInput = document.querySelector('#postListing [data-js-child="$searchInput"]');
    this.$result = document.querySelector('#postListing [data-js-child="$result"]');
    this.$productFilter = document.querySelector('[data-element="post-listing-product-filter"]');
    this.$industryFilter = document.querySelector('[data-element="post-listing-industry-filter"]');
  }

  bindEvents() {
    this.$searchInput.addEventListener('keyup', this);
    this.$searchInput.addEventListener('focus', this);
    this.$productFilter?.addEventListener('change', () => this.handleFilterChange());
    this.$industryFilter?.addEventListener('change', () => this.handleFilterChange());
  }

  async init() {
    if (this.initializing) return;
    this.initializing = true;
    if (!this.pagefind) {
      try {
        this.pagefind = await import('/_pagefind/pagefind.js');
      } catch (e) {
        this.initializing = false;
        throw e;
      }
    }
    this.initializing = false;
  }

  onfocus() {
    if (!this.initializing) {
      this.init();
    }
  }

  onkeyup(e) {
    if (e.key == 'Backspace' && this.$searchInput.value == '') {
      // If filters are active, perform a new search with just the filters
      if (this.$productFilter?.value || this.$industryFilter?.value) {
        this.search('');
      } else {
        this.resetPosts();
      }
    }
    this.debouncedSearch(this.$searchInput.value);
  }

  async debouncedSearch(term) {
    if (!term) {
      this.searched = false;
      if (this.timer) clearTimeout(this.timer);
      return;
    }

    const executeSearchFunc = () => this.search(term);

    if (this.debounceTimeoutMs > 0 && term) {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(executeSearchFunc, this.debounceTimeoutMs);
      await this.waitForApiInit();
      this.pagefind.preload(term);
    } else {
      executeSearchFunc();
    }
  }

  async waitForApiInit() {
    while (!this.pagefind) {
      this.init();
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  async search(term) {
    this.searchTerm = term || '';
    this.loading = true;
    this.searched = true;
    await this.waitForApiInit();

    const local_searchId = ++this.searchId;

    // Get filter values from dropdowns
    const productFilter = this.$productFilter?.value || '';
    const industryFilter = this.$industryFilter?.value || '';

    // Hide pagination if we're searching or filtering
    const paginationElement = document.querySelector('.pagination');
    if (paginationElement) {
      if (term || productFilter || industryFilter) {
        paginationElement.style.display = 'none';
      } else {
        paginationElement.style.display = '';
      }
    }

    // Build Pagefind search options
    const searchOptions = {
      limit: 1000,
      filters: {
        type: ['post'], // Only return posts
      },
    };

    if (productFilter || industryFilter) {
      if (productFilter) {
        searchOptions.filters.category = productFilter;
      }

      if (industryFilter) {
        searchOptions.filters.industries = industryFilter;
      }
    }

    const results = await this.pagefind.search(term || null, searchOptions);

    if (this.searchId === local_searchId) {
      this.searchResult = results;
      this.loading = false;
      this.show = results.results.length;
      this.getResult();
    }
  }

  getResult() {
    if (this.searchResult.results.length === 0 || !this.loading) this.$result.innerHTML = '';
    const results = this.searchResult.results.slice(0, this.show);
    this.renderResult(results);
  }

  renderResult(results) {
    if (results.length === 0) {
      this.$result.innerHTML = '<li id="no-posts">No posts found.</li>';
      return;
    }

    results.forEach(async (result) => {
      const data = await result.data();
      this.$result.innerHTML += this.renderHtml(data);
    });
  }

  renderHtml(data) {
    let output = `<li class="posts-list__item" data-js-children="$posts" data-js-product="${data.meta.category}" data-js-industry="${data.meta.industries}">
        <a class="posts__card card card--theme-dark u-link-reset" href="${data.url}">`;

    if (data.meta.image) {
      output += '<div class="card__media posts__card-media color-bar-container">';

      if (data.meta.pagefindThumbnailColorBars) {
        output += this.renderColorBars(data.meta.pagefindThumbnailColorBars, data.meta.pagefindThumbnailColorBarPlacement);
      }

      output += `<div class="frame frame--16-9">
                    <img src="${data.meta.image}" alt="${data.meta.image_alt}" width="2880" height="1274" loading="eager" decoding="async">
                </div>
            </div>`;
    }

    output += '<div class="card__content posts__card-content"><div class="head | stack stack--s">';

    if (data.meta.date) {
      output += `<p class="type-eyebrow-1 | card__eyebrow posts__card-eyebrow">${data.meta.date}</p>`;
    }

    if (data.meta.title) {
      output += `<h2 class="type-body-1 | card__title posts__card-title">${data.meta.title}</h2>`;
    }

    if (data.meta.thumbnailText) {
      output += `<div class="type-body-3 | card__body posts__card-body">${data.meta.thumbnailText}</div>`;
    }

    output += `
      </div><div class="bottom"><button class="btn  btn--text">
        <span class="btn__text">Learn More</span>
        <div class="btn__icon-container">
            <svg class="btn__icon" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 30 24">
                <path stroke-width="3" d="m.5 11.8333 27 .3334M17.5 2l10 10-10 10"></path>
            </svg>
            <svg class="btn__icon btn__icon--alt" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 30 24">
                <path stroke-width="3" d="m.5 11.8333 27 .3334M17.5 2l10 10-10 10"></path>
            </svg>
        </div>
      </button></div></div></a></li>`;

    return output;
  }

  resetPosts() {
    // Only reset if no filters are active
    if (!this.$productFilter?.value && !this.$industryFilter?.value) {
      // Show pagination when resetting
      const paginationElement = document.querySelector('.pagination');
      if (paginationElement) {
        paginationElement.style.display = '';
      }

      this.htmx.ajax('GET', window.location.href, {
        target: '.postListing__filter-container',
        select: '.postListing__content',
      });
    }
  }

  renderColorBars(color, placement) {
    // Create a reverse mapping for secondary bar color
    const reverseColorMap = {
      dark: 'white',
      white: 'dark',
      'dark-teal': 'light-teal',
      'light-teal': 'dark-teal',
      'light-yellow': 'dark-yellow',
      'dark-yellow': 'light-yellow',
      'light-red': 'red',
      red: 'light-red',
      'light-purple': 'purple',
      purple: 'light-purple',
      'light-blue': 'blue',
      blue: 'light-blue',
      'light-green': 'green',
      green: 'light-green',
    };

    // Extract the bar type from the placement (dual vs single)
    const barType = placement.includes('dual') ? 'dual' : 'single';
    placement = placement.replace(/(dual-|single-)/g, '');

    let output = `<div class="color-bars color-bars--${barType} color-bars--${placement}">`;

    output += `<div class="color-bars__first color-bars__color--${color}"></div>`;

    // Render second bar if necessary
    if (barType == 'dual') {
      output += `<div class="color-bars__second color-bars__color--${reverseColorMap[color]}"></div>`;
    }

    output += '</div>';

    return output;
  }

  handleFilterChange() {
    // Clear the results if no filters and no search term
    if (!this.$productFilter?.value &&
        !this.$industryFilter?.value &&
        !this.searchTerm) {
      this.resetPosts();
      return;
    }

    // Otherwise perform the search with current term
    this.search(this.searchTerm);
  }
}

registerModule('PostListingSearch', PostListingSearch);
